import { element } from "./dom.js";
import { pageRoom, type SheetLabels, sized } from "./worksheet-page.js";
import type { WorksheetPaper } from "./worksheet-state.js";

/**
 * The pages of a sheet whose parts run on from one another.
 *
 * The look-alike and vocabulary sheets fill the paper. A part starts where
 * the last one ended, and only the answer key starts a page of its own, so it
 * can be held back from a learner. The first page carries the kind of sheet
 * over its title, every later page a running head saying which sheet it is,
 * and a part that carries on over a page says so.
 *
 * Every row is drawn at a height fixed in millimetres, the way
 * `worksheet-layout.ts` lays out the character sheet, so the pages are filled
 * here a whole row at a time and no row is split across two.
 */

/** One row of a part, and its height on paper. */
export interface FlowRow {
  readonly node: HTMLElement;
  readonly height: number;

  /**
   * The least height the row can be drawn at and still hold what is in it,
   * where it has room to spare. A row without one is always drawn at its
   * height.
   */
  readonly least?: number;
}

/** A row or gap on the page being filled, and what it could give up. */
interface Held {
  readonly node: HTMLElement;
  readonly height: number;
  readonly least: number;
}

/** One part of a sheet. */
export interface FlowPart {
  /** Its number in the badge, or nothing for a part with no number. */
  readonly number?: number;
  readonly title: string;
  readonly chinese?: string;
  /** The line under the heading saying what to do. */
  readonly note?: string;
  readonly rows: readonly FlowRow[];
  /** Whether the part starts a page, as the answer key does. */
  readonly startsPage?: boolean;
}

/** What a flowed sheet is called. */
export interface FlowSheet {
  readonly paper: WorksheetPaper;
  /** The kind of sheet, over the title on the first page. */
  readonly kind: string;
  readonly kindChinese: string;
  readonly parts: readonly FlowPart[];
  readonly labels: SheetLabels;
}

/** The first page's heading: the kind of sheet over its title, and a rule. */
export const firstHeadHeight = 20;

/** A later page's running head. */
export const runningHeadHeight = 9.5;

/** A part's heading with no note, and the room each line of a note adds. */
export const partHeadHeights = { bare: 9.5, noteLine: 4 } as const;

/**
 * How far a note's column starts in from the page's edge: the badge's 6.5 mm
 * column and the 2.2 mm gap beside it, as `.cb-wf-part-head` lays them out.
 */
const noteIndent = 8.7;

/**
 * About how wide a letter of a note prints, in millimetres.
 *
 * Measured in Baloo 2 at the note's 3 mm, the sheets' notes came to between
 * 1.23 and 1.30 mm a letter. The widest of those is used, and a note this
 * counts as fitting one line fits it in print. A 汉字 takes the note's whole
 * 3 mm.
 */
const noteLetterWidth = 1.3;
const noteHanziWidth = 3;

/** The line saying a part carries on from the page before. */
export const continuedHeight = 6;

/** The space after a part's last row. */
export const partGap = 4;

/** The least that space can be drawn at, on a page drawn closer. */
export const partGapLeast = 1;

/** How wide a word of a note prints, in millimetres. */
function noteWidth(word: string): number {
  return Array.from(word).reduce(
    (sum, letter) =>
      sum + (/\p{Script=Han}/u.test(letter) ? noteHanziWidth : noteLetterWidth),
    0,
  );
}

/**
 * How many lines a note takes in a column so many millimetres wide, wrapped
 * between words the way the browser wraps it, and between 汉字.
 *
 * The heading is drawn at a fixed height and lays its title out from the
 * bottom. A note a line longer than the heading had room for pushed the title
 * up over whatever was above it. On A4 the Collocations note missed its one
 * line by 0.3 mm, and its title sat over the rule under the sheet's heading.
 */
export function noteLines(note: string, width: number): number {
  let lines = 1;
  let line = 0;
  for (const word of note.split(" ")) {
    const length = noteWidth(word);
    if (line > 0 && line + noteLetterWidth + length > width) {
      lines += 1;
      line = length;
    } else {
      line += (line > 0 ? noteLetterWidth : 0) + length;
    }
    // A run of 汉字 has no spaces in it and breaks between any two of them.
    while (line > width) {
      lines += 1;
      line -= width;
    }
  }

  return lines;
}

/** How much room a part's heading takes on a page so many millimetres wide. */
function partHeadHeight(part: FlowPart, width: number): number {
  return part.note === undefined
    ? partHeadHeights.bare
    : partHeadHeights.bare +
        partHeadHeights.noteLine * noteLines(part.note, width - noteIndent);
}

/** A block's height, and the least it can be drawn at. */
interface Squeezable {
  readonly height: number;
  readonly least: number;
}

/**
 * The heights to draw blocks at that are `over` millimetres too tall for
 * their page, each giving up the same share of what it can spare.
 *
 * A page takes the last few rows before a page break this way. Left to a
 * page of their own, they printed as a running head, one row and a great
 * deal of white. What a row can spare is the space around its contents.
 */
function squeezed(
  blocks: readonly Squeezable[],
  over: number,
): readonly number[] {
  const spare = blocks.reduce(
    (sum, block) => sum + block.height - block.least,
    0,
  );
  if (over <= 0 || spare <= 0) {
    return blocks.map((block) => block.height);
  }
  const share = Math.min(1, over / spare);

  // Rounded down to the hundredth of a millimetre a length is written at.
  // Forty rows rounded to the nearest came to 0.1 mm more than the page.
  return blocks.map(
    (block) =>
      Math.floor((block.height - (block.height - block.least) * share) * 100) /
      100,
  );
}

function hz(text: string, className = ""): HTMLElement {
  return element("span", {
    className: `cb-wf-hz ${className}`.trim(),
    text,
    attributes: { lang: "zh-Hans" },
  });
}

/** Lays out flowed sheets. */
export class SheetFlow {
  readonly #sheet: FlowSheet;

  constructor(sheet: FlowSheet) {
    this.#sheet = sheet;
  }

  /**
   * Every page, in order.
   *
   * A row that does not fit starts a new page, unless the rest of the sheet up
   * to the next page break would fit on this one with its rows drawn closer.
   * Then the rest stays here, and the rows and gaps on the page give up the
   * same share of what each can spare. A sheet whose last row missed its page
   * by a few millimetres used to print that row alone on a page of its own,
   * with a running head over it and a footer under it.
   */
  pages(): readonly HTMLElement[] {
    const room = pageRoom(this.#sheet.paper).height;
    const parts = this.#sheet.parts.filter((part) => part.rows.length > 0);
    const pages: Node[][] = [];
    let page: Node[] = [];
    let held: Held[] = [];
    let left = 0;
    let keyTitle = false;
    let pulled = false;

    const close = (): void => {
      const heights = squeezed(held, -left);
      for (const [at, one] of held.entries()) {
        sized(one.node, { height: heights[at]! });
      }
      pages.push(page);
    };
    const open = (answers: boolean): void => {
      if (page.length > 0) {
        close();
      }
      const first = pages.length === 0 || answers;
      page = [first ? this.#firstHead(answers) : this.#runningHead()];
      held = [];
      pulled = false;
      left = room - (first ? firstHeadHeight : runningHeadHeight);
    };
    const hold = (node: HTMLElement, height: number, least: number): void => {
      page.push(node);
      held.push({ node, height, least });
      left -= height;
    };
    // Whether the rest of the sheet up to the next page break fits here.
    const fits = (part: number, row: number, withHead: boolean): boolean => {
      const rest = this.#rest(parts, part, row, withHead, keyTitle);
      const spare = held.reduce(
        (sum, one) => sum + one.height - one.least,
        rest.spare,
      );

      return rest.height - left <= spare;
    };

    open(false);
    for (const [at, part] of parts.entries()) {
      if (part.startsPage === true && !keyTitle) {
        open(true);
        keyTitle = true;
      } else if (
        !pulled &&
        left < this.#headHeight(part) + part.rows[0]!.height
      ) {
        if (fits(at, 0, true)) {
          pulled = true;
        } else {
          open(false);
        }
      }
      page.push(this.#partHead(part));
      left -= this.#headHeight(part);
      for (const [index, row] of part.rows.entries()) {
        if (!pulled && row.height > left) {
          if (fits(at, index, false)) {
            pulled = true;
          } else {
            open(false);
            page.push(this.#continued(part));
            left -= continuedHeight;
          }
        }
        hold(row.node, row.height, row.least ?? row.height);
      }
      hold(element("div", { className: "cb-wf-gap" }), partGap, partGapLeast);
    }
    close();

    return pages.map((children, at) =>
      this.#page(children, at + 1, pages.length),
    );
  }

  /**
   * How tall the rest of the sheet is up to its next page break, from a row
   * of a part, and how much of that its rows and gaps could spare.
   */
  #rest(
    parts: readonly FlowPart[],
    part: number,
    row: number,
    withHead: boolean,
    keyTitle: boolean,
  ): { readonly height: number; readonly spare: number } {
    let height = 0;
    let spare = 0;
    for (let at = part; at < parts.length; at += 1) {
      const one = parts[at]!;
      if (at > part && one.startsPage === true && !keyTitle) {
        break;
      }
      if (at > part || withHead) {
        height += this.#headHeight(one);
      }
      for (const flowRow of one.rows.slice(at === part ? row : 0)) {
        height += flowRow.height;
        spare += flowRow.height - (flowRow.least ?? flowRow.height);
      }
      height += partGap;
      spare += partGap - partGapLeast;
    }

    return { height, spare };
  }

  #page(children: readonly Node[], number: number, total: number): HTMLElement {
    const { paper, labels } = this.#sheet;
    const size = pageRoom(paper).paper;
    const page = element("section", {
      className: "cb-ws-page cb-wf-page",
      attributes: {
        "data-paper": paper,
        "aria-label": `Page ${String(number)} of ${String(total)}`,
      },
      children: [
        element("div", { className: "cb-ws-body", children }),
        element("footer", {
          className: "cb-ws-footer",
          children: [
            element("a", {
              text: labels.site,
              attributes: { href: labels.address },
            }),
            element("span", {
              text: `Page ${String(number)} of ${String(total)}`,
            }),
          ],
        }),
      ],
    });

    return sized(page, { width: size.width, height: size.height, padding: 12 });
  }

  #firstHead(answers: boolean): HTMLElement {
    const head = element("header", {
      className: "cb-wf-head",
      children: [
        element("span", {
          className: "cb-wf-eyebrow",
          children: answers
            ? [document.createTextNode("Answers"), hz("答案")]
            : [
                document.createTextNode(this.#sheet.kind),
                hz(this.#sheet.kindChinese),
              ],
        }),
        element("h2", {
          className: "cb-wf-title",
          text: this.#sheet.labels.title,
        }),
      ],
    });

    return sized(head, { height: firstHeadHeight });
  }

  #runningHead(): HTMLElement {
    const head = element("header", {
      className: "cb-wf-running",
      children: [
        element("strong", { text: this.#sheet.labels.title }),
        element("span", {
          className: "cb-wf-eyebrow",
          children: [
            document.createTextNode(this.#sheet.kind),
            hz(this.#sheet.kindChinese),
          ],
        }),
      ],
    });

    return sized(head, { height: runningHeadHeight });
  }

  #headHeight(part: FlowPart): number {
    return partHeadHeight(part, pageRoom(this.#sheet.paper).width);
  }

  #partHead(part: FlowPart): HTMLElement {
    const title = element("h3", {
      className: "cb-wf-part-title",
      children: [
        document.createTextNode(part.title),
        ...(part.chinese === undefined ? [] : [hz(part.chinese)]),
      ],
    });
    const head = element("div", {
      className: "cb-wf-part-head",
      children: [
        part.number === undefined
          ? element("span")
          : element("span", {
              className: "cb-wf-badge",
              text: String(part.number),
            }),
        title,
        ...(part.note === undefined
          ? []
          : [element("p", { className: "cb-wf-part-note", text: part.note })]),
      ],
    });

    return sized(head, { height: this.#headHeight(part) });
  }

  #continued(part: FlowPart): HTMLElement {
    return sized(
      element("p", {
        className: "cb-wf-continued",
        text: `${part.title}, continued`,
      }),
      { height: continuedHeight },
    );
  }
}

/** Text in the sheet's Chinese face, for the sheets' own rows. */
export function sheetHanzi(text: string, className = ""): HTMLElement {
  return hz(text, className);
}

/** A cue: a word's pinyin, and its English where it has some. */
export function sheetCue(pinyin: string, english = ""): HTMLElement {
  return element("span", {
    className: "cb-wf-cue",
    children: [
      element("span", {
        className: "cb-wf-py",
        text: pinyin,
        attributes: { lang: "zh-Latn-CN-pinyin" },
      }),
      ...(english === ""
        ? []
        : [element("span", { className: "cb-wf-en", text: english })]),
    ],
  });
}

/** An item's number. */
export function sheetNumber(number: number): HTMLElement {
  return element("span", { className: "cb-wf-num", text: String(number) });
}

/**
 * A row of items laid out in equal columns, at a fixed height.
 * @param columns - How many items a row holds.
 * @param least - The least height a row can be drawn at, as `FlowRow` has it.
 */
export function sheetRows(
  items: readonly HTMLElement[],
  columns: number,
  height: number,
  className = "",
  least = height,
): readonly FlowRow[] {
  const rows: FlowRow[] = [];
  for (let at = 0; at < items.length; at += columns) {
    const row = element("div", {
      className: `cb-wf-row ${className}`.trim(),
      children: items.slice(at, at + columns),
    });
    row.style.setProperty("--cb-wf-columns", String(columns));
    rows.push({ node: sized(row, { height }), height, least });
  }

  return rows;
}

/** The space between a row's columns, as `.cb-wf-row` has it. */
export const columnGap = 6;

/**
 * How wide an item is on paper when it takes `span` of a row's `columns`.
 * @param room - The width of the row, the page's room inside its margins.
 */
export function columnWidth(room: number, columns: number, span = 1): number {
  const column = (room - (columns - 1) * columnGap) / columns;

  return span * column + (span - 1) * columnGap;
}

/** An item for `sheetSpannedRows`, and how many columns it takes. */
export interface SpannedItem {
  readonly node: HTMLElement;
  readonly span: number;
}

/**
 * Rows of items in equal columns, where an item too wide for one column
 * takes two or more. The items keep their order, so an item that does not fit
 * in what is left of a row starts the next one and leaves the rest empty.
 * @param columns - How many columns a row has.
 * @param least - The least height a row can be drawn at, as `FlowRow` has it.
 */
export function sheetSpannedRows(
  items: readonly SpannedItem[],
  columns: number,
  height: number,
  least = height,
): readonly FlowRow[] {
  const rows: FlowRow[] = [];
  let held: HTMLElement[] = [];
  let used = 0;
  const flush = (): void => {
    const row = element("div", { className: "cb-wf-row", children: held });
    row.style.setProperty("--cb-wf-columns", String(columns));
    row.style.setProperty("column-gap", `${String(columnGap)}mm`);
    rows.push({ node: sized(row, { height }), height, least });
    held = [];
    used = 0;
  };
  for (const { node, span } of items) {
    const taken = Math.min(Math.max(1, span), columns);
    if (used + taken > columns) {
      flush();
    }
    if (taken > 1) {
      node.style.setProperty("grid-column", `span ${String(taken)}`);
    }
    held.push(node);
    used += taken;
  }
  if (held.length > 0) {
    flush();
  }

  return rows;
}
